/**
 * Defines the {@link Circular} functional component.
 * @module
 */
import React, { FC, ReactElement, useState, useEffect } from "react";
import "./Circular.scss";
import { Header } from "../Header/Header";
import { StandardCircular } from "../StandardCircular/StandardCircular";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import * as circularApiRequests from "@api/circularApiRequests";
import { extractUrlQueryParams, safeInvoke } from "@utils";
import { CircularTypes, HeaderTypes } from "@componentTypes";

/**
 * Props interface for {@link Circular}.
 */
interface Props {
    /**
     * If true, this component is being loaded in unit testing mode.
     */
    isUnitTest?: boolean;
    /**
     * Triggered when the "add to list" feature completes.
     * For unit testing purposes only.
     * @param platform - The platform the method completed for.
     * @param isInNetwork - If true, the action was performed "in app".
     */
    onAddToListComplete?(platform: string, isInApp: boolean): void;
}

/**
 * Generates the base circular page.
 * @param props - The component props.
 * @returns the Circular component.
 */
export const Circular: FC<Props> = (props: Props): ReactElement => {
    // Get the URL params.
    const extractedUrlParams = extractUrlQueryParams();

    // State variables.
    const [headerInfo, setHeaderInfo] = useState<HeaderTypes.HeaderDefinition | undefined>(undefined);
    const [items, setItems] = useState<CircularTypes.ItemDefinition[] | undefined>(undefined);
    const [itemsInList, setItemsInList] = useState<boolean[]>([]);
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
    const [urlParams] = useState<CircularTypes.UrlParams>({
        circularId: extractedUrlParams["cir"],
        storeId: extractedUrlParams["str"],
        udid: extractedUrlParams["ifa"],
        app: extractedUrlParams["app"],
        plt: extractedUrlParams["plt"],
        inApp: extractedUrlParams["inapp"] === "1",
        preview: extractedUrlParams["preview"] === "1",
    });

    /**
     * Triggered when the window is being resized.
     */
    const handleWindowResize = (): void => {
        setWindowWidth(window.innerWidth);
    };

    /**
     * Send the Circular Telemetry events.
     * @param eventName - The event name for the events being sent.
     * @param itemId - The item ID of the item clicked by the user.
     * @param itemIndex - The index of the item displayed within the circular.
     */
    const sendTrackingEvents = (
        eventName: CircularTypes.EventName,
        itemId: string | undefined,
        itemIndex: number | undefined,
    ): void => {
        circularApiRequests
            .sendCircularEvent(
                {
                    udid: urlParams.udid!,
                    app_id: urlParams.app,
                    circular_id: urlParams.circularId!,
                    location_id: urlParams.storeId!,
                    item_id: itemId,
                    slot: itemIndex,
                    event_name: eventName,
                    submitted_at: new Date().getTime(),
                },
                props.isUnitTest,
            )
            .then();
    };

    /**
     * Attempts to send a postMessage containing a
     * DetailedListItem to the react-native SDK.
     * @param detailedListItem - The item to send in the message.
     */
    const sendReactNativePostMessage = (detailedListItem: CircularTypes.DetailedListItem): void => {
        try {
            // For react-native, we must trigger the postMessage in a
            // certain way that interacts with the "WebView" component
            // being used to render the circular.
            // @ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify(detailedListItem));
        } catch {
            // Do nothing
        }
    };

    /**
     * Gets the circular data from the API.
     */
    const getCircularData = (): void => {
        circularApiRequests
            .getCircularData(
                {
                    circularId: urlParams.circularId || "",
                    preview: urlParams.preview === true,
                },
                props.isUnitTest,
            )
            .then((response) => {
                const circularData = response.data.data.circular;
                const headerDefinition: HeaderTypes.HeaderDefinition = {
                    storeName: circularData.location.name,
                    address: circularData.location.address1,
                    city: circularData.location.city,
                    state: circularData.location.state,
                    zipCode: circularData.location.zipcode,
                    latitude: circularData.location.latitude,
                    longitude: circularData.location.longitude,
                    startDate: circularData.startDate,
                    endDate: circularData.endDate,
                    logoUrl: circularData.logoURL,
                };
                const itemDefinitionList: CircularTypes.ItemDefinition[] = [];
                const listItemsSelected: boolean[] = [];

                for (const item of circularData.items) {
                    itemDefinitionList.push({
                        title: item.title,
                        additionalDetails: item.size,
                        price: item.salePrice || item.price,
                        isWhileSuppliesLast: false,
                        imageUrl: item.imageUrl,
                        outOfNetworkActionUrl:
                            urlParams.plt && urlParams.plt.toLowerCase() === "other" ? item.itemUrl : undefined,
                        trackingId: item.trackingId,
                        brand: item.brand,
                        category: item.category,
                        upc: item.upc,
                        sku: item.sku,
                    });

                    listItemsSelected.push(false);
                }

                if (!urlParams.preview) {
                    sendTrackingEvents(CircularTypes.EventName.VIEWED, undefined, undefined);
                }

                setHeaderInfo(headerDefinition);
                setItems(itemDefinitionList);
                setItemsInList(listItemsSelected);
            });
    };

    /**
     * Triggered when an item's action button is clicked.
     * @param item - The item clicked.
     * @param index - The index of the item clicked.
     */
    const handleOnItemClicked = (item: CircularTypes.ItemDefinition, index: number) => {
        let eventName: CircularTypes.EventName = CircularTypes.EventName.ADDED_TO_LIST;

        // Cases covered:
        //     - In-Network
        //         1. In-App
        //             a. iOS
        //             b. Android
        //         2. Out-Of-App
        //     - Out-Of-Network
        if (urlParams.plt && urlParams.plt.toLowerCase() !== "other") {
            // The user is in-network.
            if (urlParams.inApp) {
                const detailedListItem: CircularTypes.DetailedListItem = {
                    product_title: item.title,
                    product_brand: item.brand,
                    product_category: item.category,
                    product_barcode: item.upc,
                    product_sku: item.sku,
                    product_discount: "",
                    product_image: item.imageUrl,
                };

                // User is In-App
                if (urlParams.plt === "ios") {
                    // iOS (Native SDK)
                    try {
                        const url = `content:${btoa(
                            JSON.stringify({
                                detailed_list_items: [detailedListItem],
                            }),
                        )}`;

                        const aTag = document.createElement("a");
                        aTag.setAttribute("href", url);
                        aTag.click();
                    } catch {
                        // Do nothing
                    }

                    // iOS (react-native)
                    sendReactNativePostMessage(detailedListItem);

                    // TODO: iOS (JS)

                    safeInvoke(props.onAddToListComplete, "ios", true);
                } else {
                    // Android (Native SDK)
                    try {
                        // For Android native, we must trigger the
                        // event by using the available "bridge" method.
                        // @ts-ignore
                        window.AdAdapted.addItemToList({
                            circularId: urlParams.circularId!,
                            trackingId: item.trackingId,
                            title: item.title,
                            brand: item.brand,
                            category: item.category,
                            upc: item.upc,
                            sku: item.sku,
                            discount: "",
                            image: item.imageUrl,
                        });
                    } catch {
                        // Do nothing
                    }

                    // Android (React Native SDK)
                    sendReactNativePostMessage(detailedListItem);

                    // TODO: JS SDK needs to be setup to support circulars.
                    // Android (JS SDK)
                    // try {
                    //     window.parent.postMessage(
                    //         JSON.stringify(detailedListItem),
                    //         "*"
                    //     );
                    // } catch {
                    //     // Do nothing
                    // }

                    safeInvoke(props.onAddToListComplete, "android", true);
                }
            } else {
                // User is Out-Of-App
                eventName = CircularTypes.EventName.ITEM_QUEUED;

                try {
                    circularApiRequests
                        .scheduleOutOfAppItemAddToList(
                            {
                                appId: urlParams.app!,
                                circularId: parseInt(urlParams.circularId!, 10),
                                idfa: urlParams.udid!,
                                item: {
                                    trackingId: item.trackingId,
                                    productTitle: item.title,
                                    productBrand: item.brand,
                                    productCategory: item.category,
                                    productBarcode: item.upc,
                                    productSku: item.sku,
                                    productDiscount: "",
                                    productImage: item.imageUrl,
                                },
                            },
                            props.isUnitTest,
                        )
                        .then();
                } catch {
                    // Do nothing
                }

                safeInvoke(props.onAddToListComplete, urlParams.plt === "ios" ? "ios" : "android", false);
            }
        } else {
            // The user is out-of-network.
            eventName = CircularTypes.EventName.REFERRAL;
        }

        // As long as we are not in "preview" mode, allow the GA and Event calls.
        if (!urlParams.preview) {
            sendTrackingEvents(eventName, item.trackingId, index);
        }

        // Update the state with the selected item,
        // so it looks selected in the view.
        setItemsInList((prevItemsInState) => {
            const newItemsInList = [...prevItemsInState];
            newItemsInList[index] = true;

            return newItemsInList;
        });
    };

    useEffect(() => {
        document.body.classList.add("default-theme");

        window.addEventListener("resize", handleWindowResize);

        // This is carry over from the circular_web project.
        // This approach attempts to get the "AdAdapted" object made available
        // by the SDK. The addItemToList method exists as a "bridge" method that
        // when called, sends the data to the native bridge so the item gets
        // added to the user's list. This is being used for Android only currently.
        // @ts-ignore
        if (typeof AdAdapted !== "undefined") {
            // @ts-ignore
            window.AdAdapted = AdAdapted;
        } else {
            // @ts-ignore
            window.AdAdapted = {
                addItemToList(info: CircularTypes.AddItemToListConsoleWarnInputs): void {
                    console.warn({
                        circularId: info.circularId,
                        trackingId: info.trackingId,
                        title: info.title,
                        brand: info.brand,
                        category: info.category,
                        upc: info.upc,
                        retailerSku: info.retailerSku,
                        discount: info.discount,
                        image: info.image,
                    });
                },
            };
        }

        getCircularData();

        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, []);

    // Returned JSX.
    return (
        <div className="Circular">
            {items && headerInfo ? <Header headerInfo={headerInfo} /> : undefined}
            {items ? (
                <StandardCircular
                    items={items}
                    itemsInList={itemsInList}
                    windowWidth={windowWidth}
                    onItemClicked={(item, index) => {
                        handleOnItemClicked(item, index);
                    }}
                />
            ) : (
                <div className="loading-indicator-container">
                    <LoadingIndicator />
                </div>
            )}
        </div>
    );
};
